import React from 'react';
import { UseFormReturn, useFormContext } from 'react-hook-form';

import { get } from 'lodash';

import { useExtendedFormContext } from 'contexts/extendedFormContext';
import { Maybe } from 'types/UtilityTypes';
import { cn } from 'utils/shadcn';

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, placeholder, onBlur, ...props }, ref) => {
    const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
      const trimmedValue = event.target.value.trim();
      event.target.value = trimmedValue;

      if (onBlur) {
        onBlur(event);
      }
    };

    const formContext = useFormContext() as Maybe<UseFormReturn<any>>;
    const extendedFormContext = useExtendedFormContext();

    const highlightDirtyField =
      !!formContext &&
      !!extendedFormContext &&
      !!props.name &&
      (() => {
        const { formState } = formContext;
        const { dirtyFields } = formState;
        const highlightDirtyFields =
          extendedFormContext.highlightDirtyFields ?? false;
        const fieldState = formContext.getFieldState(props.name!, formState);
        const isDirty = fieldState.isDirty || get(dirtyFields, props.name);

        return Boolean(isDirty && highlightDirtyFields);
      })();

    return (
      <input
        type={type}
        className={cn(
          'flex h-8 w-full rounded-[4px] border border-neutral-input-border bg-neutral-input-bg text-[13px] text-neutral-input-text',
          'px-3 py-2 ring-offset-neutral-50 scroll-mt-20',
          'file:border-0 file:bg-transparent file:text-[14px] file:font-medium',
          'placeholder:text-neutral-input-placeholder focus-visible:outline-brand focus-visible:read-only:outline-none',
          'disabled:cursor-not-allowed read-only:cursor-not-allowed read-only:bg-neutral-input-disabled-bg read-only:border-neutral-input-disabled-border read-only:text-neutral-input-disabled-text',
          className,
          highlightDirtyField &&
            'bg-warning-200 disabled:bg-warning-200/50 read-only:bg-warning-200/50'
        )}
        ref={ref}
        data-1p-ignore='true' // Prevent 1Password popup
        placeholder={props.readOnly ? '' : placeholder}
        {...props}
        onBlur={handleBlur}
      />
    );
  }
);
Input.displayName = 'Input';

export { Input };
